// The structure symbol of every strategic object (R1103, R1104, mockup
// round 054, `ui-map-interface.md` §6.2, `themes.md` §3.5).
//
// The owner picked ten sets from round 054, and each set draws all nine
// kinds: Trench Throne, Relay Spire, Forge Vent, Kelp Terrace, Deep Battery,
// Drydock Shoal, Archive Vault, Marshal Court and Coral Ward. ONE field of a
// Marks body, `structure`, names the set of all nine, so a player never mixes
// two shape languages on one map.
//
// The shape of this module is `hold-marks.ts`: a table of draw functions and
// one entry function. `shared.ts` holds the types, the id roster and the
// small path helpers; one file per set holds the art.

import type { MarkGraphics } from '../marks';
import { BLOCKS_SET } from './blocks';
import { BUOYS_SET } from './buoys';
import { DEFAULT_BREAKWATER, DEFAULT_SET } from './default';
import { ECHO_SET } from './echo';
import { GAUGE_RING_SET } from './gauge-ring';
import { HERALDIC_SET } from './heraldic';
import { LETTERS_SET } from './letters';
import { PICTOGRAM_SET } from './pictogram';
import { SILHOUETTE_SET } from './silhouette';
import { TILE_SET } from './tile';
import {
  STRUCTURE_SYMBOL_IDS, type MarkKind, type StructureInk,
  type StructureSet, type StructureSymbolId,
} from './shared';

export { STRUCTURE_KINDS, STRUCTURE_SYMBOL_IDS, structureKindOf } from './shared';
export type { MarkKind, StructureInk, StructureKind, StructureSymbolId } from './shared';
export { DEFAULT_HALOS } from './default';

/**
 * Every set, by its id.
 *
 * `default` is card 04 of round 054, the mark of today with a glyph in its
 * core. The nine other ids are the nine other cards the owner picked, and
 * each one draws all nine kinds in its own shape language.
 */
export const STRUCTURE_SETS: Record<StructureSymbolId, StructureSet> = {
  'default': DEFAULT_SET,
  // Card 01, "CHART PICTOGRAMS".
  'pictogram': PICTOGRAM_SET,
  // Card 03, "HERALDIC SHIELD".
  'heraldic': HERALDIC_SET,
  // Card 05, "SOLID SILHOUETTES".
  'silhouette': SILHOUETTE_SET,
  // Card 06, "ISOMETRIC BLOCKS".
  'blocks': BLOCKS_SET,
  // Card 07, "SONAR ECHO".
  'echo': ECHO_SET,
  // Card 09, "GAUGE RING".
  'gauge-ring': GAUGE_RING_SET,
  // Card 14, "HEX TILE BADGE".
  'tile': TILE_SET,
  // Card 18, "LETTER MARKS".
  'letters': LETTERS_SET,
  // Card 19, "IALA BUOYS".
  'buoys': BUOYS_SET,
};

/** True for a string the build carries as a structure symbol id. */
export function isStructureSymbol(s: string): s is StructureSymbolId {
  return (STRUCTURE_SYMBOL_IDS as readonly string[]).includes(s);
}

/**
 * One strategic object's mark, at its own cell centre.
 *
 * `set` is the value of the `structure` field of the Marks body in force,
 * and `kind` is the object kind the Sieges frame states. Nothing drawn here
 * moves: the atlas bakes the still mark, so the contested tell reads at
 * motion off and in greyscale (R1104).
 */
export function drawStructureMark(
  g: MarkGraphics, set: StructureSymbolId, kind: MarkKind,
  ink: StructureInk,
): void {
  // The Reef Breakwater wears the one mark of the default set in every
  // set (R2085).
  if (kind === 'breakwater') DEFAULT_BREAKWATER(g, ink);
  else STRUCTURE_SETS[set][kind](g, ink);
}
